import { type ReactElement, type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  Divider,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Modal from '@/components/common/modal';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { useBreakpointUp } from '@/hooks/use-breakpoint';

export type MenuButtonType<T> = {
  icon: ReactElement;
  key: T;
  translationKey: string;
};

export default function StandardModal<T extends string>({
  open,
  close,
  title,
  subtitle,
  avatar,
  menu = true,
  menuButtons,
  topLeftSection,
  menuSelected,
  onMenuSelect,
  onMenuOpen,
  children,
  footer,
  size,
  height,
  maxHeight,
  width,
  collapsibleMenu = false,
}: {
  open: boolean;
  close: () => void;
  title: string;
  subtitle?: ReactNode;
  avatar?: ReactNode;
  menu?: boolean;
  menuButtons?: MenuButtonType<T>[];
  topLeftSection?: ReactElement;
  onMenuOpen?: (open: boolean) => void;
  menuSelected?: T;
  onMenuSelect?: (key: T) => void;
  footer?: ReactNode;
  children: ReactNode;
  size?: 'lg' | 'md' | 'sm';
  height?: string | number;
  maxHeight?: string | number;
  width?: string | number;
  collapsibleMenu?: boolean;
}) {
  const t = useTranslations();
  const theme = useTheme();
  const isDesktop = useBreakpointUp('sm');
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const menuButtonsSafe = useMemo(() => menuButtons ?? [], [menuButtons]);
  const showMenu = menu && menuButtonsSafe.length > 0;
  const selectedKey = menuSelected ?? menuButtonsSafe[0]?.key;
  // Derive tab index from parent selection — avoid startTransition here.
  // Flow detail sockets/zustand can starve concurrent updates (same reason App.tsx
  // disables RR transitions), which made these tabs appear unclickable.
  const selectedTabIndex = selectedKey ? menuButtonsSafe.findIndex((btn) => btn.key === selectedKey) : 0;
  const tabIndex = selectedTabIndex >= 0 ? selectedTabIndex : 0;

  // On desktop, menu can be collapsible if enabled, otherwise always open
  const menuOpen = isDesktop && showMenu && (!collapsibleMenu || !menuCollapsed);

  useEffect(() => {
    onMenuOpen?.(menuOpen);
  }, [menuOpen, onMenuOpen]);

  // Desktop: show full menu if not collapsed (when collapsible) or always (when not collapsible)
  const fullMenu = isDesktop && showMenu && (!collapsibleMenu || !menuCollapsed);

  // Mobile: Full-page overlay with tabs navigation
  if (!isDesktop) {
    const menuKeys = menuButtonsSafe.map((btn) => btn.key);

    return (
      <Dialog open={open} onClose={close} fullScreen PaperProps={{ sx: { bgcolor: 'background.default' } }}>
        <Stack spacing={0} sx={{ height: '100%', overflow: 'hidden' }}>
          {/* Sticky Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1200,
              boxShadow: 'none',
              borderBottom: 'none',
              bgcolor: 'background.default',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                {avatar && <Box sx={{ display: 'flex', alignItems: 'center' }}>{avatar}</Box>}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
                    {title}
                  </Typography>
                  {subtitle && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <IconButton size="small" onClick={close} sx={{ color: 'text.primary' }}>
                <CloseIcon />
              </IconButton>
            </Stack>

            {/* Tabs Navigation */}
            {showMenu && (
              <Tabs
                value={tabIndex}
                onChange={(event, newVal) => {
                  void event;
                  const newKey = menuKeys[newVal];
                  if (newKey) {
                    onMenuSelect?.(newKey);
                  }
                }}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  'borderBottom': `1px solid ${theme.palette.divider}`,
                  'minHeight': '48px',
                  // Disabled scroll buttons still capture clicks and block tabs underneath.
                  '& .MuiTabs-scrollButtons.Mui-disabled': {
                    display: 'none',
                  },
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'flex-start',
                    gap: 0.5,
                    minHeight: '48px',
                  },
                  '& .MuiTab-root': {
                    'minWidth': 'fit-content',
                    'px': 1.5,
                    'py': 1,
                    'minHeight': '48px',
                    'textTransform': 'none',
                    'fontSize': '0.875rem',
                    'fontWeight': 500,
                    '&:focus': { outline: 'none' },
                    '&.Mui-focusVisible': { outline: 'none' },
                    'transition': 'all 0.2s ease-in-out',
                  },
                  '& .Mui-selected': {
                    fontWeight: 600,
                    color: 'primary.main',
                  },
                  '& .MuiTabs-indicator': {
                    height: 2,
                    borderRadius: '2px 2px 0 0',
                  },
                }}
              >
                {menuButtonsSafe.map((button) => (
                  <Tab
                    key={button.key}
                    label={
                      button.translationKey === 'common.profile'
                        ? 'Profile'
                        : t(button.translationKey as TranslationKey)
                    }
                    disableRipple
                    icon={button.icon}
                    iconPosition="start"
                    sx={{
                      '& .MuiTab-iconWrapper': {
                        marginRight: 0.75,
                        fontSize: '1.125rem',
                      },
                    }}
                  />
                ))}
              </Tabs>
            )}
          </Box>

          {/* Content */}
          <Fade in={true} timeout={300}>
            <Box
              sx={{
                px: 2,
                py: 2,
                overflowY: 'auto',
                flex: 1,
                pb: footer ? '80px' : 2,
              }}
            >
              {children}
            </Box>
          </Fade>

          {/* Sticky Footer */}
          {footer && (
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                width: '100%',
                bgcolor: 'background.default',
                borderTop: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
                px: 2,
                py: 2,
                zIndex: 1200,
              }}
            >
              {footer}
            </Box>
          )}
        </Stack>
      </Dialog>
    );
  }

  // Desktop: Sidebar menu layout
  return (
    <Modal
      open={open}
      handleClose={close}
      title={title}
      subtitle={subtitle}
      avatar={avatar}
      size={size || 'lg'}
      height={height || '500px'}
      maxHeight={maxHeight}
      width={width}
      footer={footer}
    >
      {showMenu ? (
        <Box
          sx={{
            height: '100%',
            display: 'grid',
            gridTemplateColumns: fullMenu ? '200px 1px minmax(0, 1fr)' : '50px 1px minmax(0, 1fr)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ overflow: 'hidden', position: 'relative', pt: collapsibleMenu ? 5 : 0 }}>
            {collapsibleMenu && (
              <IconButton
                onClick={() => setMenuCollapsed(!menuCollapsed)}
                sx={{
                  'position': 'absolute',
                  'top': 8,
                  'right': 8,
                  'zIndex': 10,
                  'bgcolor': 'background.paper',
                  'border': '1px solid',
                  'borderColor': 'divider',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  'width': 32,
                  'height': 32,
                }}
                size="small"
              >
                {menuCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            )}
            <Stack direction="row" justifyContent={'start'} sx={{ height: '100%', overflow: 'auto' }}>
              <Stack sx={{ width: '100%' }}>
                <List dense sx={{ p: '0 8px 0 8px' }}>
                  {topLeftSection}
                  {topLeftSection && <Divider component="li" />}
                  {menuButtonsSafe.map((button, i) => (
                    <ListItem
                      key={`list-button-${i}`}
                      sx={{
                        borderRadius: '7px',
                        p: 0,
                      }}
                      onClick={() => onMenuSelect?.(button.key)}
                      disablePadding
                    >
                      <ListItemButton sx={{ p: '4px 8px 4px 8px' }}>
                        <ListItemIcon
                          sx={{
                            color: selectedKey === button.key ? 'primary.main' : 'action.active',
                            minWidth: 10,
                          }}
                        >
                          {button.icon}
                        </ListItemIcon>
                        {fullMenu && (
                          <ListItemText
                            sx={{
                              textTransform: 'capitalize',
                              color: selectedKey === button.key ? 'primary.main' : 'action.active',
                              mx: 1,
                            }}
                            slotProps={{ primary: { fontSize: '1em' } }}
                            primary={
                              button.translationKey === 'common.profile'
                                ? 'Profile'
                                : t(button.translationKey as TranslationKey)
                            }
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Stack>
            </Stack>
          </Box>
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <Divider orientation="vertical" sx={{ height: '100%' }} />
          </Box>
          <Box
            sx={{
              'px': collapsibleMenu ? 0 : 3,
              'overflowY': 'auto',
              'overflowX': 'hidden',
              'position': 'relative',
              'height': '100%',
              'boxSizing': 'border-box',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: (theme) => theme.palette.grey[300],
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: (theme) => theme.palette.grey[400],
              },
            }}
          >
            {children}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            mx: 3,
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            height: '100%',
          }}
        >
          {children}
        </Box>
      )}
    </Modal>
  );
}
